// Thin typed client over the newsfeed API. Types are the generated bindings from the
// Rust `newsfeed-entities` crate (see src/api/bindings/, regenerate with `pnpm gen:types`).
// Session auth rides on an HttpOnly cookie, so every request sends credentials.

import type { Me } from './bindings/Me';
import type { RegisterRequest } from './bindings/RegisterRequest';
import type { LoginRequest } from './bindings/LoginRequest';
import type { ApiTokenInfo } from './bindings/ApiTokenInfo';
import type { CreateApiToken } from './bindings/CreateApiToken';
import type { CreatedApiToken } from './bindings/CreatedApiToken';
import type { Source } from './bindings/Source';
import type { NewSource } from './bindings/NewSource';
import type { DiscoveredFeed } from './bindings/DiscoveredFeed';
import type { OpmlImportResult } from './bindings/OpmlImportResult';
import type { Interest } from './bindings/Interest';
import type { UpsertInterest } from './bindings/UpsertInterest';
import type { FeedPage } from './bindings/FeedPage';
import type { Signal } from './bindings/Signal';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** Error carrying the HTTP status so callers (e.g. 401 → login) can branch on it. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = await res.json();
      if (data && typeof data.error === 'string') message = data.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // auth
  me: () => request<Me>('GET', '/v1/auth/me'),
  register: (req: RegisterRequest) => request<Me>('POST', '/v1/auth/register', req),
  login: (req: LoginRequest) => request<Me>('POST', '/v1/auth/login', req),
  logout: () => request<void>('POST', '/v1/auth/logout'),

  // feed
  feed: (opts: { cursor?: string; includeSaved?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.cursor) q.set('cursor', opts.cursor);
    if (opts.includeSaved) q.set('include_saved', 'true');
    const qs = q.toString();
    return request<FeedPage>('GET', `/v1/feed${qs ? `?${qs}` : ''}`);
  },
  signal: (sig: Signal) => request<void>('POST', '/v1/feed/signals', sig),

  // sources
  listSources: () => request<Source[]>('GET', '/v1/sources'),
  createSource: (s: NewSource) => request<Source>('POST', '/v1/sources', s),
  deleteSource: (id: string) => request<void>('DELETE', `/v1/sources/${id}`),
  discoverFeed: (url: string) => request<DiscoveredFeed>('POST', '/v1/sources/discover', { url }),
  importOpml: (opml: string) => request<OpmlImportResult>('POST', '/v1/sources/import', { opml }),

  // interests
  listInterests: () => request<Interest[]>('GET', '/v1/interests'),
  upsertInterest: (i: UpsertInterest) => request<Interest>('PUT', '/v1/interests', i),
  deleteInterest: (id: string) => request<void>('DELETE', `/v1/interests/${id}`),

  // api tokens
  listTokens: () => request<ApiTokenInfo[]>('GET', '/v1/tokens'),
  createToken: (t: CreateApiToken) => request<CreatedApiToken>('POST', '/v1/tokens', t),
  revokeToken: (id: string) => request<void>('DELETE', `/v1/tokens/${id}`),
};
